import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional label sent to Sentry tags for grouping (e.g. "snapshots", "jarvis"). */
  routeName?: string;
  /** Optional render override; receives the error and a reset callback. */
  fallback?: (args: { error: Error; reset: () => void }) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  eventId: string | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, eventId: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const eventId = Sentry.captureException(error, {
      tags: this.props.routeName ? { route: this.props.routeName } : undefined,
      extra: { componentStack: info.componentStack },
    });
    this.setState({ eventId });
  }

  reset = (): void => {
    this.setState({ error: null, eventId: null });
  };

  goHome = (): void => {
    this.reset();
    if (window.location.pathname !== '/') {
      window.location.assign('/');
    }
  };

  reportFeedback = (): void => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render(): React.ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }

    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold text-navy mb-2">
            Something went wrong
          </h1>
          <p className="text-gray-600 text-base mb-6">
            We hit an unexpected error and our team has been notified. You can try again or head back to your dashboard.
          </p>

          <div className="space-y-3">
            <button
              onClick={this.reset}
              className="w-full bg-navy text-white py-3 rounded-lg font-semibold hover:bg-navy-dark transition flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>
            <button
              onClick={this.goHome}
              className="w-full bg-white text-navy border-2 border-navy py-3 rounded-lg font-semibold hover:bg-navy hover:text-white transition flex items-center justify-center gap-2"
            >
              <Home className="w-5 h-5" />
              Back to Dashboard
            </button>
            {this.state.eventId && (
              <button
                onClick={this.reportFeedback}
                className="w-full text-gray-500 py-2 rounded-lg text-sm hover:text-navy transition"
              >
                Tell us what happened
              </button>
            )}
          </div>

          {import.meta.env.DEV && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-gray-500 cursor-pointer">
                Developer details
              </summary>
              <pre className="mt-2 text-xs bg-gray-50 p-3 rounded overflow-auto max-h-48">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
